"use client";
import React, { useState, useCallback } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

// all 8 compass directions in indonesian, in compass order
export const ORIENTATION_OPTIONS = [
  "Utara",
  "Timur Laut",
  "Timur",
  "Tenggara",
  "Selatan",
  "Barat Daya",
  "Barat",
  "Barat Laut",
] as const;

/**
 * Normalizes a stored orientation value into an array.
 * Legacy documents and drafts hold a single string; new ones hold an array.
 */
export function toOrientationArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === "string" && item.length > 0,
    );
  }
  if (typeof value === "string" && value.length > 0) {
    return [value];
  }
  return [];
}

interface MultiSelectOrientationProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Multi-select dropdown for property orientation (arah hadap) supporting
 * all 8 compass directions.
 */
export function MultiSelectOrientation({
  value = [],
  onChange,
  placeholder = "Select orientation",
  disabled = false,
  className,
}: MultiSelectOrientationProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const handleToggle = useCallback(
    (direction: string) => {
      const newSelection = value.includes(direction)
        ? value.filter((item: string) => item !== direction)
        : [...value, direction];
      onChange(newSelection);
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (direction: string) => {
      onChange(value.filter((item: string) => item !== direction));
    },
    [value, onChange],
  );

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              value.length > 0 ? "text-black" : "text-muted-foreground",
              disabled && "cursor-not-allowed opacity-50",
            )}
            disabled={disabled}
          >
            <div className="flex flex-1 flex-wrap gap-1 overflow-hidden">
              {value.length === 0 ? (
                <span>{placeholder}</span>
              ) : value.length <= 2 ? (
                value.map((direction: string) => (
                  <Badge
                    key={direction}
                    variant="secondary"
                    className="text-xs"
                  >
                    {direction}
                  </Badge>
                ))
              ) : (
                <span>{value.length} directions selected</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          container={containerRef.current}
          className="z-[70] w-[var(--radix-popover-trigger-width)] p-1"
          align="start"
        >
          {ORIENTATION_OPTIONS.map((direction: string) => {
            const isSelected = value.includes(direction);
            return (
              <button
                key={direction}
                type="button"
                onClick={() => handleToggle(direction)}
                className="flex w-full cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <div
                  className={cn(
                    "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "opacity-50 [&_svg]:invisible",
                  )}
                >
                  <Check className="h-3 w-3" />
                </div>
                <span>{direction}</span>
              </button>
            );
          })}
        </PopoverContent>
      </Popover>

      {/* selected directions as removable badges */}
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {value.map((direction: string) => (
            <Badge
              key={direction}
              variant="secondary"
              className="flex items-center gap-1 text-xs"
            >
              {direction}
              <button
                type="button"
                onClick={() => handleRemove(direction)}
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remove {direction}</span>
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
